/* eslint-disable react-refresh/only-export-components */
import { resolveVertical } from '@weather-story/vertical-config';
import type { VerticalId } from '@weather-story/vertical-config';
import { createBrowserRouter, Navigate } from 'react-router';
import { AdminRoute } from './components/AdminRoute';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { loadVerticalRoutes } from './verticals/registry';

/**
 * Detect the active vertical at module scope (before React renders).
 * Same logic as VerticalContext.detectVertical(), but we need it here
 * because createBrowserRouter runs outside the component tree.
 */
function detectVerticalId(): VerticalId {
  if (typeof window === 'undefined') return 'weathr';
  const params = new URLSearchParams(window.location.search);
  const override = params.get('v');
  if (override) return override as VerticalId;
  return resolveVertical(window.location.hostname).id;
}

const activeVerticalId = detectVerticalId();

/**
 * Lazy-load only the active vertical's routes via dynamic import.
 * Other verticals' route code is never downloaded.
 */
const { routes: verticalRoutes, adminRoutes: verticalAdminRoutes } =
  await loadVerticalRoutes(activeVerticalId);

function HydrateFallback() {
  return null;
}

/** Shared routes available on all verticals */
const sharedRoutes = [
  // Auth callback + onboarding
  {
    path: 'auth/callback',
    lazy: () => import('./pages/AuthCallbackPage').then((m) => ({ Component: m.AuthCallbackPage })),
  },
  {
    Component: ProtectedRoute,
    children: [
      { path: 'onboarding', element: <Navigate to="/onboarding/welcome" replace /> },
      {
        path: 'onboarding/:step',
        lazy: () => import('./pages/OnboardingPage').then((m) => ({ Component: m.OnboardingPage })),
      },
      {
        path: 'subscription/success',
        lazy: () =>
          import('./pages/SubscriptionSuccessPage').then((m) => ({
            Component: m.SubscriptionSuccessPage,
          })),
      },
    ],
  },
  // Dedicated sign-in page (nav "Sign in" points here).
  {
    path: 'login',
    lazy: () => import('./pages/LoginPage').then((m) => ({ Component: m.LoginPage })),
  },
  { path: 'register', element: <Navigate to="/login" replace /> },
  // Shared pages
  {
    path: 'about',
    lazy: () => import('./pages/AboutPage').then((m) => ({ Component: m.AboutPage })),
  },
  {
    path: 'privacy',
    lazy: () => import('./pages/PrivacyPage').then((m) => ({ Component: m.PrivacyPage })),
  },
  {
    path: 'terms',
    lazy: () => import('./pages/TermsPage').then((m) => ({ Component: m.TermsPage })),
  },
  {
    path: 'contact',
    lazy: () => import('./pages/ContactPage').then((m) => ({ Component: m.ContactPage })),
  },
  {
    path: 'licenses',
    lazy: () => import('./pages/LicensesPage').then((m) => ({ Component: m.LicensesPage })),
  },
  {
    path: 'api-docs',
    lazy: () => import('./pages/ApiDocsPage').then((m) => ({ Component: m.ApiDocsPage })),
  },
  {
    path: 'search',
    lazy: () => import('./pages/SearchPage').then((m) => ({ Component: m.SearchPage })),
  },
  // Protected user pages
  {
    Component: ProtectedRoute,
    children: [
      {
        path: 'settings',
        lazy: () => import('./pages/SettingsPage').then((m) => ({ Component: m.SettingsPage })),
      },
      {
        path: 'subscription',
        lazy: () =>
          import('./pages/SubscriptionPage').then((m) => ({ Component: m.SubscriptionPage })),
      },
      {
        path: 'payments',
        lazy: () =>
          import('./pages/PaymentHistoryPage').then((m) => ({
            Component: m.PaymentHistoryPage,
          })),
      },
      {
        path: 'profile',
        lazy: () => import('./pages/SettingsPage').then((m) => ({ Component: m.SettingsPage })),
      },
      {
        path: 'engagement',
        lazy: () => import('./pages/EngagementPage').then((m) => ({ Component: m.EngagementPage })),
      },
      {
        path: 'api-keys',
        lazy: () => import('./pages/ApiKeysPage').then((m) => ({ Component: m.ApiKeysPage })),
      },
    ],
  },
  // Admin dashboard (auth-gated)
  {
    Component: AdminRoute,
    children: [
      {
        path: 'admin',
        lazy: () => import('./components/AdminLayout').then((m) => ({ Component: m.AdminLayout })),
        children: verticalAdminRoutes,
      },
    ],
  },
  // 404
  {
    path: '*',
    lazy: () => import('./pages/NotFoundPage').then((m) => ({ Component: m.NotFoundPage })),
  },
];

export const router = createBrowserRouter([
  {
    path: '/',
    Component: Layout,
    HydrateFallback,
    children: [...verticalRoutes, ...sharedRoutes],
  },
]);
